/**
 * Client-side Logger
 * Logs important events to localStorage, backend, and optionally to a file
 * View logs at: http://localhost:3000/logs or check localStorage
 */

const LOG_STORAGE_KEY = 'app_logs';
const MAX_LOGS = 1000; // Keep last 1000 log entries
const LOG_LEVELS = {
  ERROR: 'ERROR',
  WARN: 'WARN',
  INFO: 'INFO',
  DEBUG: 'DEBUG'
};

class Logger {
  constructor() {
    this.logs = this.loadLogs();
    this.logBuffer = []; // Buffer for file writes
    this.flushInterval = null;
    this.startFileFlush();
  }

  loadLogs() {
    try {
      const stored = localStorage.getItem(LOG_STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      console.error('Failed to load logs:', e);
      return [];
    }
  }

  saveLogs() {
    try {
      // Keep only last MAX_LOGS entries
      if (this.logs.length > MAX_LOGS) {
        this.logs = this.logs.slice(-MAX_LOGS);
      }
      localStorage.setItem(LOG_STORAGE_KEY, JSON.stringify(this.logs));
    } catch (e) {
      console.error('Failed to save logs:', e);
    }
  }

  startFileFlush() {
    // Flush logs to backend every 5 seconds
    if (this.flushInterval) return;
    this.flushInterval = setInterval(() => {
      if (this.logBuffer.length > 0) {
        this.flushToBackend();
      }
    }, 5000);
  }

  async flushToBackend() {
    if (this.logBuffer.length === 0) return;
    
    const logsToSend = [...this.logBuffer];
    this.logBuffer = [];
    
    try {
      const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
      await fetch(`${apiUrl}/api/logs/write`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ logs: logsToSend }),
        signal: AbortSignal.timeout(3000) // 3 second timeout
      });
    } catch (e) {
      // If flush fails, put logs back in buffer (but limit size)
      this.logBuffer = [...logsToSend.slice(-100), ...this.logBuffer].slice(0, 500);
    }
  }

  log(level, category, message, data = null) {
    const timestamp = new Date().toISOString();
    const logEntry = {
      timestamp,
      level,
      category,
      message,
      data: data ? JSON.parse(JSON.stringify(data)) : null, // Deep clone to avoid circular refs
      userAgent: navigator.userAgent,
      url: window.location.href
    };
    
    // Calculate duration if startTime is provided in data
    if (data && typeof data.startTime === 'number') {
      const endTime = performance.now();
      logEntry.data = { ...logEntry.data, startTime: data.startTime, endTime, duration: endTime - data.startTime };
    }

    // Add to logs array
    this.logs.push(logEntry);
    this.saveLogs();

    // Add to buffer for file writing
    this.logBuffer.push(logEntry);
    if (this.logBuffer.length > 100) {
      // Flush if buffer gets too large
      this.flushToBackend().catch(() => {
        // Silently fail
      });
    }

    // Also log to console with appropriate level
    const consoleMessage = `[${level}] [${category}] ${message}`;
    if (data) {
      switch (level) {
        case LOG_LEVELS.ERROR:
          console.error(consoleMessage, data);
          break;
        case LOG_LEVELS.WARN:
          console.warn(consoleMessage, data);
          break;
        case LOG_LEVELS.DEBUG:
          console.debug(consoleMessage, data);
          break;
        default:
          console.log(consoleMessage, data);
      }
    } else {
      switch (level) {
        case LOG_LEVELS.ERROR:
          console.error(consoleMessage);
          break;
        case LOG_LEVELS.WARN:
          console.warn(consoleMessage);
          break;
        case LOG_LEVELS.DEBUG:
          console.debug(consoleMessage);
          break;
        default:
          console.log(consoleMessage);
      }
    }

    // Send critical errors immediately to backend
    if (level === LOG_LEVELS.ERROR) {
      this.sendToBackend(logEntry).catch(() => {
        // Silently fail - don't block on logging
      });
    }
  }

  async sendToBackend(logEntry) {
    try {
      const apiUrl = process.env.REACT_APP_TRADING_API_URL || 'http://localhost:5000';
      await fetch(`${apiUrl}/api/logs/client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(logEntry),
        signal: AbortSignal.timeout(2000) // 2 second timeout
      });
    } catch (e) {
      // Silently fail - logging shouldn't break the app
    }
  }

  error(category, message, data = null) {
    this.log(LOG_LEVELS.ERROR, category, message, data);
  }

  warn(category, message, data = null) {
    this.log(LOG_LEVELS.WARN, category, message, data);
  }

  info(category, message, data = null) {
    this.log(LOG_LEVELS.INFO, category, message, data);
  }

  debug(category, message, data = null) {
    this.log(LOG_LEVELS.DEBUG, category, message, data);
  }

  getLogs(filter = {}) {
    let filtered = [...this.logs];

    if (filter.level) {
      filtered = filtered.filter(log => log.level === filter.level);
    }

    if (filter.category) {
      filtered = filtered.filter(log => log.category === filter.category);
    }

    if (filter.since) {
      const sinceTime = new Date(filter.since).getTime();
      filtered = filtered.filter(log => new Date(log.timestamp).getTime() >= sinceTime);
    }

    if (filter.limit) {
      filtered = filtered.slice(-filter.limit);
    }

    return filtered;
  }

  clearLogs() {
    this.logs = [];
    this.saveLogs();
  }

  exportLogs() {
    const blob = new Blob([JSON.stringify(this.logs, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `app-logs-${new Date().toISOString()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
const logger = new Logger();

// Export convenience functions
export const logError = (category, message, data) => logger.error(category, message, data);
export const logWarn = (category, message, data) => logger.warn(category, message, data);
export const logInfo = (category, message, data) => logger.info(category, message, data);
export const logDebug = (category, message, data) => logger.debug(category, message, data);

export default logger;
